import React, { useEffect, useRef, useState } from "react";
import { Spin, Button, Divider, Table, Radio, Input, Popconfirm, Tag, Tooltip, Alert } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { faPlug } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { CDO_PROTOS } from "../../../constants/adapter";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import AdapterEditor from "../../components/adapters/AdapterEditor";
// REDUX
import {
  useLazyGetAdaptersQuery,
  useDeleteAdapterMutation,
} from "../../../redux/adapters/adapterAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { withAdapterProtocol } from "../utils/bankAdapterLinks";
import { useWizard } from "../WizardContext";
// STORE
import { RootState } from "../../../app/store";
// ENTITY LINK
import WizardEntityLink from "../WizardEntityLink";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate } from "../WizardStepGate";
// SMART INVALIDATION
import {
  ADAPTER_FIELD_RULES,
  stepsForChangedFields,
  filterEmptyOptionalSteps,
  WizardStep,
  wizardStepsFrom,
} from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import mergeAddedAdapterIds from "../utils/mergeAddedAdapterIds";
import { describeError } from "../utils/describeError";
import {
  IMPORT_FILE_ACCEPT,
  IMPORT_FILE_TYPES_LABEL,
  isImportFile,
} from "../utils/importFileTypes";
import { formatUuidT } from "../../../flatbuffers/display";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import fetchAdapterDirect from "../../../flatbuffers/api/fetchAdapterDirect";
import { downloadExport } from "../../../utils/exportDownload";
import { fetchEveryPage } from "../../../utils/fetchEveryPage";

// ************************************************ */
// TYPES ****************************************** */
interface AdapterRow {
  key: string;
  id: string;
  name: string;
  protocolType: string;
  verified?: boolean;
}

type AdapterMode = "create" | "existing" | "clone";

/**
 * Validate CDO JSON content for protocol-specific required fields.
 * Mirrors the MCE checks in isCdoValid (StepAdaptersCdoEditor) but
 * operates on raw JSON or a pre-parsed object from the API.
 */
function isCdoContentValid(
  proto: string,
  cdoRaw: string | Record<string, unknown> | null,
): boolean {
  if (!cdoRaw) return false;
  try {
    const parsed: Record<string, unknown> =
      typeof cdoRaw === "string" ? JSON.parse(cdoRaw) : cdoRaw;
    switch (proto) {
      case "MCE": {
        return !!parsed.idl_file && String(parsed.idl_file).trim() !== "";
      }
      // LN1/RVU/CGA/LOBBYVISION: full validation (duplicate mapping
      // detection) requires the CdoStateBundle — raw JSON presence is
      // sufficient for the adapter list verified status.
      case "LN1":
      case "RVU":
      case "CGA":
      case "LOBBYVISION":
        return true;
      default:
        return true;
    }
  } catch {
    return false;
  }
}

/**
 * An adapter's protocol as shown and searched, upper-cased. A FlatBuffer
 * response carries the numeric `protocolType` enum; the snake_case
 * `protocol_type` string is what the JSON wire format used, and is read only
 * when the enum is absent.
 */
function adapterProtocolName(record: any): string {
  if (record.protocolType !== undefined) {
    return (AdapterProtocol[record.protocolType as AdapterProtocol] ?? "").toUpperCase();
  }
  return (record.protocol_type || "").toUpperCase();
}

/**
 * An adapter's device type as shown and searched, upper-cased, or "" when it
 * is not known. Resolved the same way as `adapterProtocolName`: the FlatBuffer
 * `liftType` enum first, then the snake_case `lift_type` fallback.
 */
function adapterLiftTypeName(record: any): string {
  if (record.liftType !== undefined) {
    return (LiftType[record.liftType as LiftType] ?? "").toUpperCase();
  }
  return (record.lift_type || "").toUpperCase();
}

function deriveFormAction(
  editingId: string | null,
  cloneId: string | undefined,
): "create" | "edit" | "duplicate" {
  if (editingId) return "edit";
  if (cloneId) return "duplicate";
  return "create";
}

// ************************************************ */
// COMPONENT ************************************** */
export default function StepAdapters() {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    state: wizardState,
    setState: setWizardState,
    markStepCompleted,
    unmarkStepCompleted,
  } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [adapters, setAdapters] = useState<AdapterRow[]>([]);
  const [editingAdapterId, setEditingAdapterId] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);

  // Mode state
  const [mode, setMode] = useState<AdapterMode>("create");
  const [existingSearch, setExistingSearch] = useState("");
  const [existingAdapters, setExistingAdapters] = useState<any[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [loadingClone] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<string | undefined>(undefined);

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // The adapter list itself is server-side and re-hydrated from
  // `wizardState.adapterIds`. The operator's *mode selection* and any
  // typed search text or chosen clone source ARE transient — losing them
  // on reload forces a re-tour of the choose-an-action UI. Persist them
  // so the operator returns to exactly the panel they were in.
  //
  // Verification: only restore mode values that match the AdapterMode
  // union (create / existing / clone). Restore editingAdapterId only if
  // the referenced adapter still exists in this org. cloneSourceId
  // similarly must point at a known adapter — otherwise we'd open the
  // duplicate flow against an id that produces 404s.
  // Added-adapter persistence is a SETUP-time (DRAFT) concern ONLY. The hydrator
  // seeds wizardState.adapterIds from bank-referenced adapters — the
  // authoritative org↔adapter link. A published (ACTIVE) org's adapters are
  // fully determined by its banks, so it must show exactly that set and never a
  // draft: an ACTIVE org has no pending unbanked additions, and honoring a draft
  // here would let a stale global-pool snapshot (captured in an earlier
  // all-adapters session) re-inject every other org's adapters into the list —
  // the "all 15 adapters show for a 3-adapter org" bug. So for ACTIVE orgs we
  // neither persist nor restore added adapters; the empty draft below also
  // overwrites (self-heals) any such stale snapshot on the next save.
  //
  // For DRAFT orgs we persist the delta — adapterIds minus the bank-referenced
  // set — so adapters the operator added but hasn't attached to a bank survive a
  // reload.
  const persistAddedAdapters = wizardState.orgStatus !== "ACTIVE";
  const referencedAdapterIdSet = new Set<string>(Object.values(wizardState.adapterIdByBank ?? {}));
  const draftAddedAdapterIds = persistAddedAdapters
    ? wizardState.adapterIds.filter((id) => !referencedAdapterIdSet.has(id))
    : [];

  useWizardStepDraft<{
    mode: AdapterMode;
    existingSearch: string;
    cloneSourceId: string | undefined;
    editingAdapterId: string | null;
    addedAdapterIds: string[];
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Adapters,
    scopeId: "adapters",
    scopeType: "step",
    draft: {
      mode,
      existingSearch,
      cloneSourceId,
      editingAdapterId,
      addedAdapterIds: draftAddedAdapterIds,
    },
    onRestore: (saved) => {
      // Union the persisted added-adapter ids back into wizard state so
      // not-yet-referenced adapters reappear — DRAFT orgs only (see above).
      // Uses `prev` because this state update is async and may batch with the
      // hydrator's own write.
      if (persistAddedAdapters) {
        setWizardState((prev) => {
          const merged = mergeAddedAdapterIds(prev.adapterIds, saved.addedAdapterIds);
          return merged === prev.adapterIds ? prev : { ...prev, adapterIds: merged };
        });
      }
      // Best-effort id set for restoring the clone/edit selection below —
      // `wizardState` is the render-time snapshot, so fold in the restored ids
      // too (the setWizardState above hasn't applied yet).
      const knownIds = new Set(
        persistAddedAdapters
          ? mergeAddedAdapterIds(wizardState.adapterIds, saved.addedAdapterIds)
          : wizardState.adapterIds,
      );
      if (saved.mode === "create" || saved.mode === "existing" || saved.mode === "clone") {
        setMode(saved.mode);
      }
      if (typeof saved.existingSearch === "string") setExistingSearch(saved.existingSearch);
      if (typeof saved.cloneSourceId === "string" && knownIds.has(saved.cloneSourceId)) {
        setCloneSourceId(saved.cloneSourceId);
      }
      if (typeof saved.editingAdapterId === "string" && knownIds.has(saved.editingAdapterId)) {
        setEditingAdapterId(saved.editingAdapterId);
      }
    },
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [triggerGetAdapters] = useLazyGetAdaptersQuery();
  const [triggerDeleteAdapter, { isLoading: isLoadingDelete }] = useDeleteAdapterMutation();

  // ************************************************ */
  // REFS ****************************************** */
  const importInputRef = useRef<HTMLInputElement>(null);
  // Keeps a current snapshot of adapters for use inside stale closures
  const adaptersRef = useRef<AdapterRow[]>([]);
  // Track which adapter IDs have been loaded to avoid re-fetching on
  // unrelated wizard state changes that produce a new array reference.
  const prevAdapterIdsRef = useRef<string>("");
  useEffect(() => {
    adaptersRef.current = adapters;
  }, [adapters]);

  // ************************************************ */
  // EXISTING/CLONE ADAPTER LOGIC ****************** */
  const fetchExistingAdapters = async () => {
    setLoadingExisting(true);
    try {
      const token = await GETJwtToken();
      // Adapters are global and the server clamps every page, so the picker
      // walks the pages instead of showing only the newest slice of them.
      const allAdapters = await fetchEveryPage((page, limit) =>
        triggerGetAdapters({ page, limit, searchAndFilterString: "", token }).unwrap(),
      );
      setExistingAdapters(allAdapters);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepAdapters] fetch existing adapters failed:", err);
      setExistingAdapters([]);
      setStepError("Failed to load existing adapters — check your connection");
    }
    setLoadingExisting(false);
  };

  useEffect(() => {
    if (mode === "existing" || mode === "clone") {
      fetchExistingAdapters();
    }
  }, [mode, wizardState.serverId]);

  const handleUseExisting = (adapter: any) => {
    const adapterId = formatUuidT(adapter.id) || adapter.id;
    if (!adapterId) return;
    if (adapters.some((a) => a.id === adapterId)) return;

    // Resolve protocol name for display (FlatBuffer uses numeric enum, JSON uses string)
    const proto =
      adapter.protocolType !== undefined
        ? (AdapterProtocol[adapter.protocolType as AdapterProtocol] ?? "")
        : (adapter.protocol_type ?? "");

    // Adapters are shared/global — reusing one does not reassign a server.
    const newRow: AdapterRow = {
      key: adapterId,
      id: adapterId,
      name: String(adapter.name || adapterId),
      protocolType: proto,
    };
    setAdapters((prev) => [...prev, newRow]);
    setWizardState((prev) => {
      const updatedIds = structuredClone(prev.adapterIds);
      updatedIds.push(adapterId);
      return {
        ...prev,
        adapterIds: updatedIds,
        adapterNames: { ...prev.adapterNames, [adapterId]: newRow.name },
      };
    });
    markStepCompleted(2);
  };

  const handleCloneAdapter = (adapterId: string) => {
    // Normalize to a string id. cloneSourceId flows into AdapterEditor's
    // existingAdapterId, which becomes an RTK Query arg — RTK JSON.stringifies
    // query args to build the cache key, and a raw UuidT (bigint upper/lower
    // halves) would throw "Do not know how to serialize a BigInt", surfacing
    // as "Load error: Do not know how to serialize a BigInt" on clone (LIFT-186).
    const normalizedId = formatUuidT(adapterId) || adapterId;
    setCloneSourceId(normalizedId);
    setMode("create");
  };

  const filteredExisting = existingAdapters.filter((a: any) => {
    if (!existingSearch) return true;
    const q = existingSearch.toLowerCase();
    return (
      (a.name || "").toLowerCase().includes(q) ||
      String(a.version || "")
        .toLowerCase()
        .includes(q) ||
      adapterProtocolName(a).toLowerCase().includes(q) ||
      adapterLiftTypeName(a).toLowerCase().includes(q)
    );
  });

  // ************************************************ */
  // REMOVE / EDIT ********************************* */
  const handleRemoveAdapter = (adapterId: string) => {
    setAdapters((prev) => prev.filter((a) => a.id !== adapterId));
    setWizardState((prev) => ({
      ...prev,
      adapterIds: prev.adapterIds.filter((id: string) => id !== adapterId),
    }));

    // An adapter leaving the configuration — whether Remove detached it or
    // Delete destroyed it, both land here — dangles every reference to it.
    // Banks bind a lift group to an adapter, and Channel Modifiers, Lifts,
    // Debounce, BACnet, Notifications and Actions all derive their message
    // vocabulary from it. This handler used to unmark nothing downstream at
    // all, which is how a stale-complete BACnet step survived the removal
    // of the adapter it was keyed to. MonitorGrid and Review have no
    // adapter-specific content of their own, but are included anyway:
    // SetupWizardPage's forward-jump guard (`step <= currentStep ||
    // isStepCompleted(step) || isStepCompleted(step - 1)`) is a single
    // immediately-prior check rather than a sequential walk, so any step
    // left complete inside the range — MonitorGrid included — becomes a
    // one-click hop-in target that skips past Notifications/Actions from
    // anywhere, and a stale-complete Review shows a false "finish" and lets
    // the operator jump past everything this removal just invalidated.
    unmarkStepCompleted(...wizardStepsFrom(WizardStep.Banks));

    if (adapters.length <= 1) {
      unmarkStepCompleted(2);
    }
  };

  const handlePermanentlyDeleteAdapter = async (adapterId: string) => {
    setStepError(null);
    try {
      const token = await GETJwtToken();
      await triggerDeleteAdapter({ id: adapterId, token }).unwrap();
      handleRemoveAdapter(adapterId);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("[StepAdapters] DELETE adapter failed:", err);
      // LiftCloud refuses the delete while any bank still references the
      // adapter, naming them in `message`. Swallowing that into a generic
      // string left the operator with a failure and no way to act on it.
      setStepError(`Failed to delete adapter — ${describeError(err)}`);
    }
  };

  const handleEditAdapter = (adapterId: string) => {
    // eslint-disable-next-line no-console
    console.log("[StepAdapters] handleEditAdapter:", adapterId);
    setEditingAdapterId(adapterId);
    setCloneSourceId(undefined);
    setMode("create");
  };

  const cancelEdit = () => {
    setEditingAdapterId(null);
    setCloneSourceId(undefined);
  };

  // ************************************************ */
  // ADAPTER EDITOR CALLBACKS ********************** */
  const handleAdapterSaveSuccess = (
    adapterId: string,
    adapterName: string,
    protocolType: string,
    verified?: boolean,
    changedFields?: string[],
    validationMessage?: string,
  ) => {
    // Persist name + verified status so both survive page navigation
    setWizardState((prev) => ({
      ...prev,
      adapterVerified: { ...prev.adapterVerified, [adapterId]: verified === true },
      adapterNames: { ...prev.adapterNames, [adapterId]: adapterName },
      adapterValidations: {
        ...prev.adapterValidations,
        [adapterId]: { valid: verified === true, message: validationMessage ?? "Valid" },
      },
    }));

    if (editingAdapterId) {
      const newProtocolType = protocolType.toUpperCase();
      // Banks already bound to this adapter take on its (possibly changed)
      // protocol now, not on the next Drafts -> Resume — Lifts & Floors keys
      // its OPC inputs off this map.
      setWizardState((prev) => ({
        ...prev,
        ...withAdapterProtocol(prev, editingAdapterId, newProtocolType),
      }));
      setAdapters((prev) =>
        prev.map((a) =>
          a.id === editingAdapterId
            ? {
                ...a,
                name: adapterName,
                protocolType: newProtocolType,
                verified,
              }
            : a,
        ),
      );
      // Run downstream invalidation only when verification succeeded. We
      // deliberately keep the editor mounted in edit mode (editingAdapterId
      // stays set) regardless of outcome: the user expects further changes —
      // such as adding another one-hot-exclusion group — to surface the
      // "Update Adapter" button again. Clearing editingAdapterId here flips
      // formAction to "create" while leaving the form fields populated with
      // the just-saved adapter's data, which silently turns the save button
      // into "+ Add Adapter" — clicks then create a duplicate adapter and
      // unsaved local cdoState is lost if the user navigates to Review and
      // hits Complete Setup. Exiting edit mode is the user's job via Cancel.
      if (verified !== false) {
        // Smart downstream invalidation. The AdapterEditor's onSaveSuccess
        // hands back the names of fields that actually changed since
        // hydration (see AdapterEditSnapshot in AdapterEditor.tsx).
        // We pass them straight into stepsForChangedFields, which maps
        // each name to its rule-defined wizard steps and returns the
        // sorted, deduped union. Cosmetic-only rename → empty array →
        // no unmark; CDO change → Banks/Notifications/Actions; protocol
        // change → everything downstream. When changedFields is
        // undefined (older save paths or future regressions in the
        // editor) we fall back to the legacy broad unmark so we stay
        // strictly no-worse-than the original behavior.
        if (changedFields !== undefined) {
          const rawStepsToInvalidate = stepsForChangedFields(changedFields, ADAPTER_FIELD_RULES);
          // Don't invalidate Notifications/Actions/MonitorGrid when the user
          // has no content in them — those steps auto-complete and forcing
          // a re-walk over an empty page is friction without value.
          const stepsToInvalidate = filterEmptyOptionalSteps(rawStepsToInvalidate, {
            notifications: wizardState.notificationIds.length > 0,
            actions: wizardState.actionCatalogIds.length > 0,
            monitorGrid: wizardState.monitorGridId !== null,
          });
          if (stepsToInvalidate.length > 0) {
            unmarkStepCompleted(...stepsToInvalidate);
          }
        } else {
          // Legacy fallback when the editor didn't pass `changedFields`: we
          // know something was saved but not what, so every step that can
          // depend on adapter shape is re-opened. The hand-written list this
          // replaces stopped at BACnet — it began as the literal
          // `(4, 5, 6, 7)` for Banks/Lifts/Debounce/BACnet under the
          // pre-ChannelModifiers numbering, and the insertion at index 5
          // silently shifted it. A stated range cannot shift again.
          // Gapless through Review on purpose: SetupWizardPage's
          // forward-jump guard (`step <= currentStep ||
          // isStepCompleted(step) || isStepCompleted(step - 1)`) makes any
          // step left complete inside the range a one-click hop-in target
          // from anywhere, so omitting one — Users, say — would let a
          // single click land on Notifications and skip Lifts entirely.
          unmarkStepCompleted(...wizardStepsFrom(WizardStep.Banks));
        }
      }
    } else {
      const newRow: AdapterRow = {
        key: adapterId,
        id: adapterId,
        name: adapterName,
        protocolType: protocolType.toUpperCase(),
        verified,
      };
      setAdapters((prev) => [...prev, newRow]);
      setWizardState((prev) => {
        const updatedIds = structuredClone(prev.adapterIds);
        updatedIds.push(adapterId);
        return {
          ...prev,
          adapterIds: updatedIds,
          adapterNames: { ...prev.adapterNames, [adapterId]: adapterName },
        };
      });
      if (verified !== false) {
        markStepCompleted(2);
      } else {
        unmarkStepCompleted(2);
      }
    }
    setCloneSourceId(undefined);
  };

  // ************************************************ */
  // IMPORT / EXPORT HANDLERS ********************** */
  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  /**
   * Import an org-level export (SQL/JSON/FlatBuffer binary or a v2.x server .dat) via the shared
   * /import/organization/analyze + /import/organization/execute pipeline.
   * Auto-resolves any adapter conflicts by replacing the existing record.
   */
  const importOrgExport = async (file: File): Promise<void> => {
    const orgId = wizardState.organizationId;
    if (!orgId) {
      setStepError("Cannot import — no organization selected.");
      return;
    }
    const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
    const token = await GETJwtToken();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("parent_org_id", orgId);

    const analyzeResp = await fetch(`${baseUrl}/import/organization/analyze`, {
      method: "POST",
      headers: { authorization: token },
      body: formData,
    });
    if (!analyzeResp.ok) {
      const body = await analyzeResp.json().catch(() => null);
      const detail = body?.message ?? `status ${analyzeResp.status}`;
      throw new Error(`Analysis failed: ${detail}`);
    }
    const analyze = await analyzeResp.json();
    const analysisId: string | undefined = analyze?.data?.id;
    if (!analysisId) {
      throw new Error("Analysis succeeded but returned no analysis_id");
    }
    const conflicts: { imported_name: string; imported_version: string }[] =
      analyze?.data?.adapter_conflicts ?? [];

    const executeResp = await fetch(`${baseUrl}/import/organization/execute`, {
      method: "POST",
      headers: { authorization: token, "Content-Type": "application/json" },
      body: JSON.stringify({
        analysis_id: analysisId,
        resolutions: conflicts.map((c) => ({
          adapter_name: c.imported_name,
          adapter_version: c.imported_version,
          action: "replace",
        })),
      }),
    });
    if (!executeResp.ok) {
      const body = await executeResp.json().catch(() => null);
      const detail = body?.message ?? `status ${executeResp.status}`;
      throw new Error(`Execute failed: ${detail}`);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = "";
    try {
      if (isImportFile(file.name)) {
        await importOrgExport(file);
      } else {
        setStepError(`Unsupported file type. Accepted: ${IMPORT_FILE_TYPES_LABEL}`);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepAdapters] import adapter from file failed:", err);
      setStepError(
        err instanceof Error
          ? `Import failed: ${err.message}`
          : "Failed to import adapter. Check the file format and try again.",
      );
    }
  };

  const handleExportAdapter = async (record: AdapterRow) => {
    if (!wizardState.organizationId) {
      setStepError("Cannot export — no organization selected.");
      return;
    }
    try {
      // Delegate to the shared /export/stage/2 pipeline with an adapter_id
      // filter. This emits the same FlatBuffer binary (.bin) that the
      // bottom-of-page LNX export produces — single canonical format.
      await downloadExport({
        orgId: wizardState.organizationId,
        stage: 2,
        includeDeps: false,
        adapterId: record.id,
        entityName: record.name,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepAdapters] export adapter failed:", err);
      setStepError(err instanceof Error ? err.message : "Failed to export adapter.");
    }
  };

  // ************************************************ */
  // HYDRATION ************************************* */
  useEffect(() => {
    const idsKey = wizardState.adapterIds.join(",");
    if (idsKey === prevAdapterIdsRef.current) return;
    prevAdapterIdsRef.current = idsKey;

    const loadExistingAdapters = async () => {
      if (wizardState.adapterIds.length === 0) return;
      const token = await GETJwtToken();
      const rows = await Promise.all(
        wizardState.adapterIds.map(async (id) => {
          // Use adaptersRef (not captured adapters) to avoid stale closure
          const current = adaptersRef.current.find((a) => a.id === id);
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data = (await fetchAdapterDirect(id, token)) as any;

            // If the fetch returned nothing (schema hash mismatch, JSON fallback
            // failure, network hiccup), preserve what we already know rather than
            // computing verified=false from null data.
            if (!data) {
              return {
                key: id,
                id,
                name: current?.name || wizardState.adapterNames?.[id] || id,
                protocolType: current?.protocolType || "",
                verified: current?.verified ?? wizardState.adapterVerified[id],
              } as AdapterRow;
            }

            // FlatBuffer path: protocolType is numeric enum; JSON path: protocol_type is string
            const isFlatBufferT = typeof data?.protocolType === "number";
            const proto = isFlatBufferT
              ? (AdapterProtocol[data.protocolType as AdapterProtocol] ?? "").toUpperCase()
              : (data?.protocol_type || "").toUpperCase();
            // Name fallback chain: API response → local state → wizard state → UUID
            const name: string =
              data?.name || current?.name || wizardState.adapterNames?.[id] || id;

            let cdoRaw: string | Record<string, unknown> | null = null;
            if (isFlatBufferT) {
              cdoRaw = data?.cdoInputJson ?? null;
            } else if (data?.cdoInput) {
              cdoRaw = data.cdoInput as Record<string, unknown>;
            }
            const needsCdo = CDO_PROTOS.has(proto);
            const hasValidName = Boolean(data?.name) && data.name !== id;

            // Priority: local state > wizard state > data-based heuristic.
            // CDO protocols must pass content validation (e.g. MCE idl_file).
            let verified: boolean | undefined =
              current?.verified ?? wizardState.adapterVerified[id];
            if (verified === undefined) {
              if (needsCdo) {
                if (cdoRaw) {
                  // CDO data returned by the server → run full content validation.
                  verified = isCdoContentValid(proto, cdoRaw);
                } else if (proto === "MCE") {
                  // MCE strictly requires CDO content (idl_file). Without CDO data
                  // we can't confirm the adapter is fully configured → stay Pending.
                  verified = undefined;
                } else if (hasValidName) {
                  // LN1/RVU/CGA/LOBBYVISION: a persisted name is sufficient — this
                  // matches AdapterEditor.verifyAdapterSave, which only requires
                  // cdoJson when allowed_messages context demands it. After a
                  // successful Verify + Submit the adapter must continue to read
                  // as Verified on revisit, even if cdoInputJson is absent from
                  // the read-back response.
                  verified = true;
                } else {
                  verified = undefined;
                }
              } else {
                verified = hasValidName;
              }
            } else if (verified && needsCdo && cdoRaw !== null) {
              // Only re-validate CDO content when the server actually returned
              // CDO data. If cdoRaw is null (field absent in response), trust
              // the existing verified state rather than downgrading it to false.
              verified = isCdoContentValid(proto, cdoRaw);
            }

            return {
              key: id,
              id,
              name,
              protocolType: proto,
              verified,
            } as AdapterRow;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[StepAdapters] Failed to fetch adapter", id, err);
            // Preserve locally-known row if fetch fails, then wizard-state name, then UUID
            return (
              current || {
                key: id,
                id,
                name: wizardState.adapterNames?.[id] || id,
                protocolType: "",
                verified: wizardState.adapterVerified[id],
              }
            );
          }
        }),
      );
      setAdapters(rows);
    };
    loadExistingAdapters();
  }, [wizardState.adapterIds]);

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const adapterColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: AdapterRow) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {name}
          <WizardEntityLink entityType="adapter" entityId={record.id} />
        </span>
      ),
    },
    { title: "Protocol", dataIndex: "protocolType", key: "protocolType" },
    {
      title: "Status",
      dataIndex: "verified",
      key: "verified",
      width: 120,
      render: (verified: boolean | undefined) => {
        if (verified === true) return <Tag color="success">Verified</Tag>;
        if (verified === false)
          return (
            <Tooltip title="Save could not be verified. Edit and re-save this adapter.">
              <Tag color="warning">Not Verified</Tag>
            </Tooltip>
          );
        return <Tag color="default">Pending</Tag>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_: unknown, record: AdapterRow) => (
        <div style={{ display: "flex", gap: 4 }}>
          <WizardOpGate op="update">
            <Button type="link" size="small" onClick={() => handleEditAdapter(record.id)}>
              Edit
            </Button>
          </WizardOpGate>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleExportAdapter(record)}
          >
            Export
          </Button>
          <WizardOpGate op="delete">
            <Button type="link" size="small" onClick={() => handleRemoveAdapter(record.id)}>
              Remove
            </Button>
            <Popconfirm
              title="Permanently delete this adapter from the database? This cannot be undone."
              onConfirm={() => handlePermanentlyDeleteAdapter(record.id)}
              okText="Yes, Delete Forever"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" loading={isLoadingDelete}>
                Delete
              </Button>
            </Popconfirm>
          </WizardOpGate>
        </div>
      ),
    },
  ];

  const existingColumns = [
    {
      title: "Name",
      key: "name",
      render: (_: unknown, record: any) => String(record.name || ""),
    },
    {
      title: "Version",
      key: "version",
      render: (_: unknown, record: any) => String(record.version || ""),
    },
    {
      title: "Protocol",
      key: "protocol_type",
      render: (_: unknown, record: any) => adapterProtocolName(record),
    },
    {
      title: "Device Type",
      key: "lift_type",
      render: (_: unknown, record: any) => adapterLiftTypeName(record) || "—",
    },
    {
      title: "Action",
      key: "action",
      render: (_: unknown, record: any) => {
        const recordId = formatUuidT(record.id) || record.id;
        const alreadyAdded = adapters.some((a) => a.id === recordId);
        if (mode === "existing") {
          return (
            <WizardOpGate op="create">
              <Button
                size="small"
                type="primary"
                disabled={alreadyAdded}
                onClick={() => handleUseExisting(record)}
              >
                {alreadyAdded ? "Added" : "Use This"}
              </Button>
            </WizardOpGate>
          );
        }
        return (
          <WizardOpGate op="create">
            <Button
              size="small"
              type="primary"
              loading={loadingClone}
              onClick={() => handleCloneAdapter(recordId)}
            >
              Clone &amp; Edit
            </Button>
          </WizardOpGate>
        );
      },
    },
  ];

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingDelete}>
      <div style={{ padding: "16px" }}>
        {stepError && (
          <Alert
            type="error"
            showIcon
            closable
            message={stepError}
            onClose={() => setStepError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <DrawerRowDivider icon={faPlug} title={t("general.configuration")} />
        {/* MODE SELECTOR */}
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6">
                <Radio.Group
                  value={mode}
                  onChange={(e) => {
                    setMode(e.target.value);
                    setEditingAdapterId(null);
                    setCloneSourceId(undefined);
                  }}
                >
                  <Radio value="create">Create New</Radio>
                  <Radio value="existing">Use Existing</Radio>
                  <Radio value="clone">Clone Existing</Radio>
                </Radio.Group>
                <input
                  ref={importInputRef}
                  type="file"
                  accept={IMPORT_FILE_ACCEPT}
                  style={{ display: "none" }}
                  onChange={handleImportFile}
                />
                <WizardOpGate op="create">
                  <Button icon={<UploadOutlined />} onClick={handleImportClick}>
                    Import
                  </Button>
                </WizardOpGate>
              </div>
            </div>
          </div>
        </div>
        <br />
        {/* EXISTING / CLONE PANELS */}
        {(mode === "existing" || mode === "clone") && (
          <>
            <DrawerRowDivider
              icon={faPlug}
              title={mode === "existing" ? "Select Existing Adapter" : "Clone an Adapter"}
            />
            <div className={`drawer__box__${theme}`}>
              <p style={{ opacity: 0.7, marginBottom: 12 }}>
                {mode === "existing"
                  ? "Choose an adapter already configured on this server."
                  : "Select an adapter to clone. Its settings and CDO mappings will be copied into a new adapter you can edit."}
              </p>
              <Input
                placeholder="Search by name, version, protocol, or device type..."
                value={existingSearch}
                onChange={(e) => setExistingSearch(e.target.value)}
                allowClear
                style={{ marginBottom: 12, maxWidth: 400 }}
              />
              <Table
                dataSource={filteredExisting.map((a: any) => ({
                  ...a,
                  key: formatUuidT(a.id) || a.id,
                }))}
                columns={existingColumns}
                loading={loadingExisting}
                pagination={false}
                size="small"
                locale={{ emptyText: "No adapters found for this server" }}
              />
            </div>
          </>
        )}

        {/* CREATE / EDIT FORM — Unified AdapterEditor */}
        {mode === "create" && (
          // AdapterEditor is shared with Edge Management, so gate it whole: its
          // Save updates when editing an adapter and creates otherwise.
          <WizardOpGate op={editingAdapterId ? "update" : "create"}>
            <AdapterEditor
              mode="wizard"
              existingAdapterId={editingAdapterId || cloneSourceId}
              formAction={deriveFormAction(editingAdapterId, cloneSourceId)}
              onSaveSuccess={handleAdapterSaveSuccess}
              onCancel={editingAdapterId || cloneSourceId ? cancelEdit : undefined}
            />
          </WizardOpGate>
        )}

        {/* ADAPTERS TABLE */}
        {adapters.length > 0 && (
          <>
            <br />
            <DrawerRowDivider icon={faPlug} title="Added Adapters" />
            <div className={`drawer__box__${theme}`}>
              <Table
                dataSource={adapters}
                columns={adapterColumns}
                pagination={false}
                size="small"
                locale={{ emptyText: "No adapters added yet" }}
              />
            </div>
          </>
        )}

        {/* EXPORT BUTTON */}
        <Divider />
        <div className={`drawer__box__${theme} `}>
          <div className="container generalStyles__flexCenter">
            <div className="row">
              <WizardExportButton stage={2} orgId={wizardState.organizationId} />
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
}
